import {
  PRESET_RANGE_LIST,
  RELATIVE_RANGE_MAX_AMOUNT,
  RELATIVE_RANGE_UNIT_LIST,
} from "@/shared/domain/boards/boardTimeRangeMode";
import {
  INTERACTION_ENTITY_TYPES,
  INTERACTION_TIME_UNITS,
} from "@/shared/domain/boards/interactionTypes";
import {
  DAYS_OF_WEEK,
  NUMERIC_AGGREGATIONS,
  PERIODS,
} from "@/shared/domain/metrics";
import { MAX_VALUE_COUNTS_LIMIT } from "@/shared/domain/query/QueryRecord";

import { withoutServerInjectedFields } from "../toolInput";

/**
 * The board definition schema the chat authors against, as the flat JSON
 * Schema `client_create_board` declares. Every tool's name, description and
 * input schema is sent with every request in a thread, so only that tool
 * carries the definition's shapes: `client_dry_run_board` declares the same
 * top-level keys and `client_update_board` takes single panels and stages,
 * and both point at the create tool's schema for each shape.
 *
 * Hand-authored, like every sibling tool. The board feature's zod schema
 * (`src/Boards/definition/`) is the runtime validator;
 * `src/Boards/definition/authoringSchema.test.ts` holds this schema to the zod
 * schema's property names, enum values, and required fields, and
 * `src/Boards/templates/chartTemplates.authoringSchema.test.ts` holds its
 * template names to the templates the parser accepts. The schema is flat
 * (variant members optional, the variants told apart in prose) because the
 * first test records Bedrock rejecting the `oneOf` plus `const` shape zod
 * generates. Its root is an object for the same reason.
 *
 * A shape used in several places is declared in full at one property and
 * named in a sentence everywhere else, since each repeat is paid for on
 * every request. The test above needs each zod property name declared once
 * anywhere in this schema, which that satisfies.
 *
 * The prose says what a schema cannot, once, and names the
 * `get_board_authoring_guide` chapter that holds the rest: the filter
 * comparators live in `authoring-workflow` (`authoringSchema.test.ts` reads
 * that chapter for the comparators this schema does not list), each source's
 * result columns in that source's chapter, chart binding rules in
 * `chart-specs`.
 *
 * `additionalProperties` stays off at the top level: the server adds
 * `_compression_intent` to every tool's input schema.
 */

/** The board's time range, in the words both the create and update tools use. */
export const TIME_RANGE_DESCRIPTION =
  `The board's time range: one of the preset strings ${PRESET_RANGE_LIST}, ` +
  '{"kind": "custom", "start", "end"} with each bound a date "YYYY-MM-DD" ' +
  'or a date and time "YYYY-MM-DDTHH:MM[:SS[.sss]]", written without "Z" ' +
  "or an offset and read in the board's time zone (a date end includes its " +
  "whole day, a time end is the instant the range stops, and the range " +
  "must end after it starts), or " +
  '{"kind": "relative", "amount": N, "unit": U} for the most recent N units ' +
  `ending when the board is opened (N a whole number from 1 to ${RELATIVE_RANGE_MAX_AMOUNT}, ` +
  `U one of ${RELATIVE_RANGE_UNIT_LIST}; a month counts as 30 days and a year as 365). ` +
  "What it limits for each kind of source is in the guide " +
  'chapter "authoring-workflow", section "The definition\'s top-level members".';

/**
 * The filters shape, stated once for a stage body's `filters`; the comparators
 * and value forms per type live in the guide chapter the text names, where
 * `authoringSchema.test.ts` checks them against the zod schema's filter
 * types, comparators and match modes.
 */
const FILTERS_DESCRIPTION =
  "Optional filters narrowing which entities match: " +
  '{"filters": [{"type", "field", "comparator", "values"}], "match_mode": ' +
  '"AND" | "OR"}. Filter types: "string", "numeric", "date", "boolean", ' +
  '"set", "enum", "identity", "metric"; each type\'s comparators and value ' +
  'forms are in the guide chapter "authoring-workflow", section "Filters". ' +
  'Example: {"filters": [{"type": "string", "field": "name", "comparator": ' +
  '"EQUALS", "values": ["hard_brake"]}], "match_mode": "AND"}. A row ' +
  "holds the values it is written with; rows narrowed by a value the " +
  'viewer chooses are a "control-filter" stage that reads this one ' +
  '(guide chapter "controls").';

/** The units a count of ticks is in, wherever a time or a duration is
 *  written as `{"ticks", "unit"}`. */
const TIME_UNITS = [...INTERACTION_TIME_UNITS];

/** The members of a duration written as `{"ticks", "unit"}`. */
const DURATION_PROPERTIES = {
  ticks: { type: "string" },
  unit: { enum: TIME_UNITS },
};

/** The entity kinds an entity.query searches, a value control's entity
 *  field belongs to, and a field control offers the fields of. */
const ENTITY_QUERY_TARGETS = [
  "events",
  "sessions",
  "datasets",
  "collections",
  "files",
  "devices",
];

/** A column computed from one row: an output name and a SQL expression. */
const NAMED_EXPRESSION_ITEMS = {
  type: "object",
  required: ["name", "expression"],
  properties: {
    name: { type: "string", minLength: 1 },
    expression: { type: "string", minLength: 1 },
  },
};

/** Declared in full at `stages[].body.valueType`. */
const VALUE_TYPE = {
  type: "object",
  required: ["type"],
  description:
    "value-control only (required): the kind of value the control holds. " +
    'A value type: {"type": "string" | "boolean" | "number" | "integer", ' +
    '"nullable"?}, or for a point in time {"type": "instant", "unit", ' +
    '"epoch", "nullable"?}. "unit" is the length of one tick ' +
    '(seconds down to nanoseconds). "epoch" names the zero point the ' +
    'ticks count from, "unix" for the Unix epoch; two instants compare ' +
    "only when their epochs are equal.",
  properties: {
    type: { enum: ["string", "boolean", "number", "integer", "instant"] },
    nullable: { type: "boolean" },
    unit: { enum: TIME_UNITS },
    epoch: { type: "string" },
  },
};
const VALUE_TYPE_REFERENCE = {
  type: "object",
  description:
    "A value type, as a value control's `valueType` (`stages[].body.valueType`) declares it.",
};

/** Declared in full at `stages[].body.domain.options[].value`. */
const TAGGED_VALUE = {
  type: "object",
  required: ["type"],
  description:
    'A tagged value: {"type": "string" | "boolean" | "number", "value": ' +
    '<that JSON primitive>}; {"type": "integer", "value": "<decimal ' +
    'string>"}; {"type": "instant", "ticks": "<decimal string>", "unit", ' +
    '"epoch"}; or {"type": "null"}. An integer and an instant\'s ' +
    "ticks are strings so that no digit is lost.",
  properties: {
    type: {
      enum: ["null", "string", "boolean", "number", "integer", "instant"],
    },
    value: {},
    ticks: { type: "string" },
    unit: { enum: TIME_UNITS },
    epoch: { type: "string" },
    provenance: {
      type: "object",
      description:
        "Present only on an instant copied from a tool result. Copy it " +
        "unchanged; never write one.",
      required: ["topicId", "partitionId", "timelineSource", "offsetNs"],
      properties: {
        topicId: { type: "string" },
        partitionId: { type: "string" },
        timelineSource: { type: "string" },
        offsetNs: { type: "string" },
      },
    },
  },
};
const TAGGED_VALUE_REFERENCE = {
  type: "object",
  description:
    "A tagged value, as `stages[].body.domain.options[].value` declares it.",
};

const STAGE_BODY_SCHEMA: Record<string, unknown> = {
  type: "object",
  required: ["kind"],
  description:
    "What one stage does. Set `kind` and include only that kind's members; a " +
    "member of another kind makes the stage invalid. Four kinds read the " +
    'platform and take no inputs: "entity.query" (target, filters, ' +
    "aggregate, fields, sort, limit) searches entities; " +
    '"metric.aggregate" (metric, aggregation, period, groupBy, filters) ' +
    "reads one value per calendar period, or one per period for each " +
    'value of a field; "metric.values" (metrics, filters) ' +
    "reads each named metric's published value for every session in the " +
    'time range; "topic.data" (topicName, scope, fields) decodes topic data in ' +
    'the browser. Two kinds read several inputs: "sql" (sql) runs ' +
    "SQL over one or more, and " +
    '"combine" (strategy, columns) joins two or more on ' +
    "key columns, as-of joins them by time, or unions their rows. " +
    "Nine kinds each do " +
    'one thing to the rows of one input: "filter" (where) keeps rows, ' +
    '"compute" (set, keep, drop) ' +
    'computes and chooses columns, "unnest" (column, as, indexColumn, ' +
    'keepEmpty) turns a list into one row per item, "limit" (rows) keeps ' +
    'the first rows, "window" (partitionBy, orderBy, add) adds columns ' +
    'computed over ordered rows, "group" (by, aggregates) aggregates, ' +
    '"sort" (orderBy) orders, "resample" (timeColumn, every, series, ' +
    "values, tolerance) puts each series on a regular time grid, and " +
    '"downsample" (intervalColumn, every, series, values) keeps each ' +
    "interval's first and last rows and the rows with its lowest and " +
    "highest values. Conditions and expressions in these nine kinds are " +
    "DuckDB SQL over one row of the input, with no window function, " +
    "aggregate, subquery or unnest. Each kind's result columns are in its " +
    "guide chapter. A board write is refused while a guide chapter one of " +
    "its stages requires is not in context; the get_board_authoring_guide " +
    "description says which chapter each stage requires, such as " +
    '"stages" for a kind that reads other stages. Column names are ' +
    "compared without regard to case. Two kinds are controls, shown above " +
    "the canvas, whose result is what the viewer chooses: " +
    '"value-control" (label, valueType, multi, required, default, domain) ' +
    'holds one or several values, and "field-control" (label, target or ' +
    'topic, default, options) holds a field name. "control-filter" ' +
    "(column, columnHolds) keeps the rows of its first input whose column " +
    "holds a value chosen in the value control its second input names, " +
    "and every row while that control has no value and is not required. " +
    "Directly after a source, or after a control-filter that narrows one, " +
    "it narrows the source's request when the request can carry the " +
    "filter. Otherwise it filters the rows after they arrive. Once its " +
    "control has a value, the stage fails instead with " +
    "control-filter-before-summary-unsupported when it reads a source " +
    "that keeps its first rows or summarizes them (an entity.query with " +
    "limit or aggregate, or a metric.aggregate) directly, or after " +
    "control-filters that narrow it, and the request cannot carry the " +
    "filter, or reads such a source through filter, sort and compute " +
    "stages, none of which computes the filtered column. Through a " +
    "compute that writes the filtered column, it filters the rows after " +
    "they arrive. " +
    "SQL, row expressions, chart spec strings and panel titles read a " +
    "control as $name, ${name} or ${name.label}; there a $ before a letter " +
    "or underscore is always a reference, so write a literal $ as $$. A " +
    "control needs a `default` when it is `required`, is a field control, " +
    "or is read in SQL, a row expression or a chart spec.",
  properties: {
    kind: {
      type: "string",
      enum: [
        "entity.query",
        "metric.aggregate",
        "metric.values",
        "topic.data",
        "sql",
        "combine",
        "filter",
        "compute",
        "unnest",
        "limit",
        "window",
        "group",
        "sort",
        "resample",
        "downsample",
        "value-control",
        "field-control",
        "control-filter",
      ],
    },
    where: {
      type: "string",
      description:
        "filter only (required): the condition a row must satisfy to be " +
        "kept, as a SQL expression over the row's columns, e.g. " +
        "\"mode = 'AUTO' AND fix_type >= 4\".",
    },
    set: {
      type: "array",
      description:
        'compute only: columns to compute, each {"name": "<output column>", ' +
        '"expression": "<SQL expression over the row>"}. A name an input ' +
        "column already has replaces that column; any other is added after " +
        "the input's columns. An expression reads the input's columns only, " +
        "so a column computed from another computed column takes a second " +
        "compute stage. The names in `set` are distinct. A compute declares at least one " +
        "of `set`, `keep` and `drop`.",
      items: NAMED_EXPRESSION_ITEMS,
    },
    keep: {
      type: "array",
      items: { type: "string" },
      description:
        "compute only (not []): the only input columns to pass through. Every " +
        "column `set` computes is in the output whether `keep` names it or " +
        "not. Declare `keep` or `drop`, not both; with neither, every input " +
        "column passes through. (A combine's `strategy.keep` is a different " +
        "member.)",
    },
    drop: {
      type: "array",
      items: { type: "string" },
      description:
        "compute only (not []): input columns to leave out. It may not name a column " +
        "`set` computes, since every computed column is in the output.",
    },
    column: {
      type: "string",
      description:
        "unnest (required): the list column to turn into one row per " +
        "item, in list order. Every other column is repeated on each row. " +
        "control-filter (required): the column of the first input whose " +
        "value a row must hold.",
    },
    columnHolds: {
      enum: ["value", "entity-id"],
      description:
        'control-filter only: "value", the default, when the column holds ' +
        "values of the control's `valueType`. " +
        '"entity-id" when the column holds ids of the entities the ' +
        "control's `entity-field` domain names (session_id for sessions), " +
        "and a row is kept when that entity's field holds a chosen value: " +
        "events, topic data and metric values by a session's custom field.",
    },
    as: {
      type: "string",
      description:
        "unnest only: the name of the column that receives the item. " +
        "Defaults to the list column's own name.",
    },
    indexColumn: {
      type: "string",
      description:
        "unnest only: the name of a column that receives the item's " +
        "position in its list, counted from 1. This name and `as` must " +
        "differ from each other and from the input's other columns.",
    },
    keepEmpty: {
      type: "boolean",
      description:
        "unnest only: when true, a row whose list is empty or null " +
        "produces one row with a null item, where it otherwise produces " +
        "none.",
    },
    rows: {
      type: "integer",
      minimum: 1,
      description:
        "limit only (required): how many rows to keep. (An entity.query " +
        "limits its own rows with its `limit` member.)",
    },
    partitionBy: {
      type: "array",
      items: { type: "string" },
      description:
        "window only: SQL expressions whose values divide the rows into " +
        'partitions, e.g. ["session_id"]. Omit it (not []) to treat every ' +
        "row as one partition.",
    },
    orderBy: {
      type: "array",
      description:
        "window (required) and sort (required): the order of rows, at " +
        'least one entry, the first entry first. Each is {"by": "<SQL expression over the ' +
        'row>", "direction": "ascending" | "descending", "nulls": "first" ' +
        '| "last"}; direction defaults to ascending and nulls to last. ' +
        'Spelled out here, unlike an entity.query `sort` ("asc" | "desc"). ' +
        "A window orders the rows of each partition by it, and rows that " +
        "tie keep the order the stage received them in.",
      items: {
        type: "object",
        required: ["by"],
        properties: {
          by: { type: "string", minLength: 1 },
          direction: { type: "string", enum: ["ascending", "descending"] },
          nulls: { type: "string", enum: ["first", "last"] },
        },
      },
    },
    add: {
      type: "array",
      description:
        "window only (required): the columns to add, each computed over " +
        "the row's partition in the declared order, under a distinct name " +
        "no input column has. " +
        '{"name", "op": "row_number"} is the row\'s position in its ' +
        'partition, from 1. {"name", "op": "lag", "value": "<expression>", ' +
        '"offset": <n>} is the value `offset` rows earlier (1 when ' +
        'omitted), null where there is none. {"name", "op": "count", ' +
        '"where": "<condition>"} counts the partition\'s rows so far that ' +
        'satisfy the condition (every row when omitted). {"name", "op": ' +
        '"sum", "value": "<expression>"} is the running sum so far.',
      items: {
        type: "object",
        required: ["name", "op"],
        properties: {
          name: { type: "string", minLength: 1 },
          op: { type: "string", enum: ["row_number", "lag", "count", "sum"] },
          value: { type: "string" },
          offset: { type: "integer", minimum: 1 },
          where: { type: "string" },
        },
      },
    },
    by: {
      type: "array",
      description:
        'group only: the grouping columns, each {"name": "<output ' +
        'column>", "expression": "<SQL expression over the row>"}. One ' +
        "output row per distinct combination; omit it (not []) for one " +
        "row over the whole input. The `by` and `aggregates` names are " +
        "distinct. (Inside `sort` and `orderBy`, `by` is one string.)",
      items: NAMED_EXPRESSION_ITEMS,
    },
    aggregates: {
      type: "array",
      description:
        'group only (required): each {"name": "<output column>", "op": ' +
        '<aggregate>, "value": "<SQL expression over the row>"}. "count" ' +
        'counts rows and takes no value; "valid" counts the rows where the ' +
        'value is present; "distinct" counts distinct values; "sum", ' +
        '"mean", "median", "min" and "max" compute over the value.',
      items: {
        type: "object",
        required: ["name", "op"],
        properties: {
          name: { type: "string", minLength: 1 },
          op: {
            type: "string",
            enum: [
              "count",
              "sum",
              "mean",
              "median",
              "min",
              "max",
              "valid",
              "distinct",
            ],
          },
          value: { type: "string" },
        },
      },
    },
    timeColumn: {
      type: "string",
      minLength: 1,
      description:
        "resample only (required): the input's timestamp column (`time` " +
        "on a topic.data stage). The output carries the grid times under " +
        "the same name.",
    },
    intervalColumn: {
      type: "string",
      minLength: 1,
      description:
        "downsample only (required): the column to divide into intervals, " +
        "a timestamp column (`time` on a topic.data stage) when `every` is " +
        "a duration or a numeric column such as a distance when `every` is " +
        "a number. Rows with no value in it (null, or a number that is not " +
        "finite) are left out.",
    },
    every: {
      required: ["ticks", "unit"],
      description:
        "resample (required): the grid interval, " +
        '{"ticks": "<whole number above zero as a decimal string>", ' +
        '"unit"}, e.g. {"ticks": "100", "unit": "ms"}. ' +
        "downsample (required): the width of one interval, a duration in " +
        "the same form over a timestamp `intervalColumn` or a number above " +
        "zero in that column's unit over a numeric one, e.g. 10. A duration " +
        "is at most 366 days. Where grid times and intervals start is in " +
        'the guide chapter "time-series-stages".',
      properties: DURATION_PROPERTIES,
    },
    series: {
      type: "array",
      items: { type: "string" },
      description:
        "resample (required) and downsample (required): the columns whose values " +
        "tell one series from another, each resampled or divided into " +
        'intervals on its own, e.g. ["session_id", "session_name"]. [] ' +
        "treats every row as one series. A resample carries them to the " +
        "output; a downsample returns the input rows it keeps, unchanged " +
        "and with every column.",
    },
    values: {
      type: "array",
      description:
        "resample (required, at least one): the columns to read at " +
        'each grid time, each {"name": "<input column>", "method": <method>}, ' +
        "each output under its own name; a column takes one role among " +
        "`series`, `timeColumn` and `values`. downsample (required, at least " +
        'one): numeric column names as plain strings, e.g. ["speed"]. ' +
        "(A filter row's `values` is a different member.)",
      items: {
        required: ["name", "method"],
        properties: {
          name: { type: "string", minLength: 1 },
          method: {
            type: "string",
            enum: ["backward", "forward", "nearest", "linear", "low-pass"],
            description:
              '"linear" and "low-pass" need a numeric column. What each ' +
              'method reads is in the guide chapter "time-series-stages", ' +
              'section "Resampling onto a time grid".',
          },
        },
      },
    },
    tolerance: {
      type: "object",
      required: ["ticks", "unit"],
      description:
        'resample only: {"ticks", "unit"}, in the form `every` takes; ' +
        "leaves a value null where its rows are too far away. How far is " +
        "too far for each method is in the guide chapter " +
        '"time-series-stages", section "Resampling onto a time grid". ' +
        "(A combine's `time.tolerance` is a different member.)",
      properties: DURATION_PROPERTIES,
    },
    metrics: {
      type: "array",
      items: { type: "string" },
      description:
        "metric.values only (required, 1 to 100, each once): the metric " +
        'names to read, e.g. ["distance_driven", "shock.count"].',
    },
    target: {
      type: "string",
      enum: ENTITY_QUERY_TARGETS,
      description:
        "entity.query (required): which kind of entity to search. " +
        "field-control: the kind of entity whose properties and custom " +
        "fields the control offers, as an alternative to `topic`.",
    },
    topic: {
      type: "string",
      description:
        "field-control only: the topic whose schema fields the control " +
        "offers, as an alternative to `target`. A field control declares " +
        "exactly one of `target` and `topic`.",
    },
    label: {
      type: "string",
      description:
        "value-control and field-control only: the control's caption. " +
        "Without it the control shows the display name of the custom " +
        "field its `domain` names, or else its name.",
    },
    valueType: VALUE_TYPE,
    multi: {
      type: "boolean",
      description:
        "value-control only: true lets the viewer choose several values.",
    },
    required: {
      type: "boolean",
      description:
        "value-control only: true makes the stages and panels that read " +
        "the control wait for a value. A control-filter of a control that " +
        "is not required and has no value keeps every row.",
    },
    default: {
      description:
        "value-control: a tagged value, such as " +
        '{"type": "string", "value": "drive"} or {"type": ' +
        '"integer", "value": "9007199254740993"}, and {"type": "null"} ' +
        "only when valueType.nullable is true; an array of them when " +
        "`multi` is true. field-control: one field name, which must be " +
        "one of its `options` when it lists them, and otherwise a field " +
        "of its `topic` or `target`, or the dry run fails with " +
        "control-value-unusable.",
    },
    domain: {
      type: "object",
      description:
        "value-control only: where the choices come from; without it the " +
        "viewer types the value. " +
        '{"kind": "static", "options": [{"value": <tagged value>, ' +
        '"label"}]} lists them. {"kind": "column", "column": ' +
        '"<column>"} offers each distinct value of a column of the ' +
        "control's one input, which its `inputs` names; that stage may " +
        "not read this control, directly or through the stages it reads. " +
        '{"kind": "entity-field", "target", "field"} offers the values of ' +
        "a field of a kind of entity, spelled as that entity spells it " +
        '("device_id", "custom.<field_name>"); which fields offer ' +
        "choices, and how a custom field supplies them, is in the guide " +
        'chapter "controls", section "Declaring a control".',
      properties: {
        kind: { enum: ["static", "column", "entity-field"] },
        column: { type: "string" },
        field: { type: "string" },
        target: {
          type: "string",
          enum: ENTITY_QUERY_TARGETS,
        },
        options: {
          type: "array",
          items: {
            type: "object",
            required: ["value", "label"],
            properties: {
              value: TAGGED_VALUE,
              label: { type: "string" },
            },
          },
        },
      },
    },
    options: {
      type: "array",
      description:
        "field-control only, at least one entry: the field names the " +
        "control offers, in place of every field of its `target` or " +
        "`topic`. A value control lists its choices in `domain`.",
      items: {
        type: "object",
        required: ["value", "label"],
        properties: {
          value: { type: "string" },
          label: { type: "string" },
        },
      },
    },
    filters: {
      type: "object",
      required: ["filters"],
      description:
        FILTERS_DESCRIPTION +
        " Applies to entity.query, metric.aggregate, metric.values, and " +
        "(inside scope) topic.data. entity.query and topic.data filter " +
        'fields are entity-relative (a custom field is "custom.<name>"), ' +
        'though a sessions filter also takes "dataset.dataset_id", ' +
        '"device.device_id" and "collection.collection_id" with the prefix ' +
        '(guide chapter "entity-queries", section "Field tokens"); ' +
        'the metric kinds are entity-prefixed ("session.custom.<name>", ' +
        '"session.name", "device.device_id"). (Rows of another stage are ' +
        'filtered by a "filter" stage; a pivot has its own `filter`.)',
    },
    aggregate: {
      type: "object",
      required: ["op"],
      description:
        'entity.query only: {"op": "count", "groupBy"?: "<name>"} counts ' +
        "the matching entities instead of returning their rows, once per " +
        "value of `groupBy` when it is given. `groupBy` is one of the names " +
        'the target accepts (guide chapter "entity-queries", section "Sort ' +
        'and group names"), or "$name" naming a field control declared with ' +
        'the same "target". Omit it to return entity rows.',
      properties: {
        op: { type: "string", enum: ["count"] },
        groupBy: { type: "string", minLength: 1 },
      },
    },
    fields: {
      type: "array",
      description:
        "entity.query without aggregate: the columns to return, in order; " +
        'the names each target takes are in the guide chapter "entity-queries", ' +
        'section "What `fields` changes"; omit for every column. ' +
        "topic.data (required): objects " +
        '{"name": "<column>", "path": ["<component>", ...]} naming the topic ' +
        "schema fields to decode; the naming rules are in the guide chapter " +
        '"topic-data-and-maps". A path may be the one component "$name" ' +
        'naming a field control declared with the same "topic", whose ' +
        'chosen field, split on its dots, replaces it under the same "name"; ' +
        "a stage a map binds may not use such a path.",
    },
    sort: {
      type: "object",
      description:
        'entity.query without aggregate only: {"by": "<name>", ' +
        '"direction": "asc" | "desc"}, where `by` is one of the names the ' +
        'target accepts (guide chapter "entity-queries", section "Sort and ' +
        'group names"). Abbreviated here, unlike a sort ' +
        'stage\'s `orderBy` ("ascending" | "descending").',
      required: ["by", "direction"],
      properties: {
        by: { type: "string" },
        direction: { type: "string", enum: ["asc", "desc"] },
      },
    },
    limit: {
      type: "integer",
      minimum: 1,
      description:
        "entity.query only: the most rows to return, or with a grouped " +
        `aggregate the most groups, at most ${MAX_VALUE_COUNTS_LIMIT.toLocaleString("en-US")} ` +
        "(largest counts first). (To limit another stage's rows, add a " +
        '"limit" stage with `rows`.)',
    },
    metric: {
      type: "string",
      description:
        "metric.aggregate only (required): the metric's dotted name.",
    },
    aggregation: {
      type: "string",
      enum: [...NUMERIC_AGGREGATIONS],
      description:
        "metric.aggregate only (required): how the values in each period combine.",
    },
    period: {
      type: "string",
      enum: [...PERIODS],
      description: "metric.aggregate only (required): the calendar period.",
    },
    groupBy: {
      type: "string",
      description:
        "metric.aggregate only: the field that splits each period into one " +
        'row per value, either "device.device_id" or a String, Enum or ' +
        'Boolean custom field written "session.custom.<name>" or ' +
        '"device.custom.<name>". The rows carry the value in a column named ' +
        'as `groupBy` spells it (guide chapter "metric-queries", section ' +
        '"metric.aggregate").',
    },
    scope: {
      type: "object",
      required: ["target", "filters"],
      description:
        'topic.data only: {"target": "sessions" | "devices" | "datasets" | ' +
        '"files", "filters": <filters over that kind>} reads, within the ' +
        "board's time range, the topic's data that belongs to the matched " +
        "entities. Omit it to read the whole topic within the time range. Which " +
        'columns each target adds is in the guide chapter "topic-data-and-maps", ' +
        'section "Columns".',
      properties: {
        target: {
          type: "string",
          enum: ["sessions", "devices", "datasets", "files"],
        },
        filters: {
          type: "object",
          description:
            "The same filters object as the body's `filters`, over the " +
            "entities of `target`.",
        },
      },
    },
    topicName: {
      type: "string",
      description:
        'topic.data only (required): the topic to read (e.g. "/gps/fix").',
    },
    sql: {
      type: "string",
      description:
        "sql only (required): one DuckDB statement that reads each input " +
        "as a table named by that stage's `name`. Write a sql stage only " +
        "for what the other kinds cannot express (guide chapter " +
        '"stages", section "Choosing a stage"), and read that chapter\'s ' +
        'section "SQL on this platform" first: general DuckDB knowledge does ' +
        "not cover this platform's columns, types and stage rules. A " +
        'control is read as $name, unquoted (guide chapter "controls").',
    },
    strategy: {
      type: "object",
      required: ["kind"],
      description:
        "combine only (required): how the stage's inputs come together. " +
        '{"kind": "join"} joins their rows on declared column pairs and ' +
        "puts each input's columns side by side; the stage's first input is " +
        "the one every other input is matched against. " +
        '{"kind": "as-of"} attaches to each row of the first input the row ' +
        "of every other input whose time is closest to that row's time in " +
        "the declared direction (an as-of join). " +
        '{"kind": "union"} puts their rows one after another, every row of ' +
        "every input kept, and declares no keys; the inputs share at least " +
        "one column name, and a name two inputs carry has one type in both. A " +
        "combine reads two or " +
        "more inputs, which are the stage's own `inputs`.",
      properties: {
        kind: { type: "string", enum: ["join", "as-of", "union"] },
        keep: {
          type: "string",
          enum: ["rows-in-every-input", "every-row-of-the-first-input"],
          description:
            'join and as-of (required): "rows-in-every-input" keeps a row ' +
            'only where every input matched; "every-row-of-the-first-input" ' +
            "keeps every row of the first input and leaves the other " +
            "inputs' columns null where nothing matched. (A compute " +
            "stage's `keep` is a list of columns.)",
        },
        on: {
          type: "array",
          description:
            "join and as-of (required): one entry per input after the " +
            'first, in input order. Each entry is {"input": "<stage id>", ' +
            '"equalColumns": [{"firstInputColumn": "<column of the first input>", ' +
            '"inputColumn": "<column of that input>"}, ...]}; every pair ' +
            "must hold for a row to match, and the two column names may " +
            "differ. Under as-of the pairs are extra equal keys and may be " +
            "empty; each entry also carries `time`. Which id columns to pair " +
            'is in the guide chapter "stages", section "Combine stages".',
          items: {
            type: "object",
            required: ["input", "equalColumns"],
            properties: {
              input: { type: "string", minLength: 1 },
              equalColumns: {
                type: "array",
                items: {
                  type: "object",
                  required: ["firstInputColumn", "inputColumn"],
                  properties: {
                    firstInputColumn: { type: "string", minLength: 1 },
                    inputColumn: { type: "string", minLength: 1 },
                  },
                },
              },
              time: {
                type: "object",
                required: ["firstInputColumn", "inputColumn", "direction"],
                description:
                  "as-of only (required): the timestamp column on each " +
                  "side (`time` on a topic.data stage) and the direction " +
                  "in which this input's matched row lies from each " +
                  "first-input row's time.",
                properties: {
                  firstInputColumn: { type: "string", minLength: 1 },
                  inputColumn: { type: "string", minLength: 1 },
                  direction: {
                    type: "string",
                    enum: ["backward", "forward", "nearest"],
                  },
                  tolerance: {
                    type: "object",
                    required: ["ticks", "unit"],
                    description:
                      "Optional: the farthest a row may be from the time, " +
                      '{"ticks": "<whole number as a decimal string>", ' +
                      '"unit"}. A row farther away counts as no row. Omit ' +
                      "it to accept any distance.",
                    properties: DURATION_PROPERTIES,
                  },
                },
              },
            },
          },
        },
      },
    },
    columns: {
      type: "object",
      description:
        "combine only: what becomes of a column two inputs both carry, and " +
        "of one that only some of them carry. Omit it for the defaults. (A table panel's " +
        "`columns` and a pivot's `columns` are different members.)",
      properties: {
        absent: {
          type: "string",
          enum: ["fill-with-null", "shared-columns-only"],
          description:
            'union only. "fill-with-null" (the default) emits the column ' +
            "once and writes an explicit null for the inputs that lack it; " +
            '"shared-columns-only" keeps the columns every input carries ' +
            "and names the ones it left out.",
        },
        collision: {
          type: "string",
          enum: ["qualify-the-later-input", "keep-the-first"],
          description:
            'join and as-of. "qualify-the-later-input" (the default) emits the ' +
            "first input's column under its own name and each later " +
            "input's as \"<that input's stage name>.<column>\", so nothing " +
            'is lost; "keep-the-first" emits the first input\'s alone and ' +
            "names every column it dropped.",
        },
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Shapes used in several places
//
// Each is declared in full at the one property its comment names; its
// `_REFERENCE` counterpart, a one-sentence description, stands everywhere else.
// ---------------------------------------------------------------------------

/** Declared in full at `markLineages[].dimensions[].source`. */
const SOURCE_FIELD = {
  type: "object",
  required: ["bindingName", "field"],
  description:
    'A source field: {"bindingName": "<binding name>", "field": "<column of ' +
    'the stage that binding reads>"}.',
  properties: { bindingName: { type: "string" }, field: { type: "string" } },
};
const SOURCE_FIELD_REFERENCE = {
  type: "object",
  description: "A source field {bindingName, field}.",
};

/** Declared in full at `markLineages[].dimensions[].output`. */
const OUTPUT_FIELD = {
  type: "object",
  required: ["lineage", "field"],
  description:
    'An output field: {"lineage": "<this mark lineage\'s id>", "field": ' +
    '"<the name the tooltip and an interaction\'s keys read the value under>"}. ' +
    "Where a spec transform computes the value, the field is the name it " +
    "writes (an aggregate's or fold's `as`, a stack's start and end).",
  properties: { lineage: { type: "string" }, field: { type: "string" } },
};
const OUTPUT_FIELD_REFERENCE = {
  type: "object",
  description: "An output field {lineage, field}.",
};

/** How a calendar grouping buckets instants. Declared in full at `selectionBindings[].dimensions[].calendar`. */
const CALENDAR_GROUPING = {
  type: "object",
  required: ["unit", "epoch"],
  description:
    "How instants are grouped into calendar units, in the board's calendar " +
    "(its `calendar` member's time zone and week start). A group dimension " +
    "mapping's `calendar` must equal the `calendar` of the mark lineage " +
    "dimension it maps.",
  properties: {
    unit: { enum: ["yearmonthdate", "yearmonth", "hours", "day", "yearweek"] },
    epoch: { type: "string" },
  },
};
const CALENDAR_GROUPING_REFERENCE = {
  type: "object",
  description:
    "A calendar grouping, as `selectionBindings[].dimensions[].calendar` declares it.",
};

/** Where numeric bins start and how wide they are. Declared in full at `selectionBindings[].dimensions[].binGrid`. */
const BIN_GRID = {
  type: "object",
  required: ["origin", "unit"],
  description:
    "A bin grid, the exact start and width of the spec's numeric bins: " +
    '{"origin", "unit"}, each a tagged value: `origin` a number, integer ' +
    "or instant, and `unit`, the bin width, a positive number or integer.",
  properties: {
    origin: TAGGED_VALUE_REFERENCE,
    unit: TAGGED_VALUE_REFERENCE,
  },
};
const BIN_GRID_REFERENCE = {
  type: "object",
  description:
    "A bin grid, as `selectionBindings[].dimensions[].binGrid` declares it.",
};

/** Declared in full at a panel's `selectionBindings[].dimensions`. */
const GROUP_DIMENSION_MAPPINGS = {
  type: "array",
  description:
    "Only when the selection or detail comes from a rendered group of a chart's mark lineage " +
    '(an interaction whose subject has kind "group" and a markLineageId): one entry ' +
    "per dimension of that mark lineage, named by the dimension's id, saying " +
    "which field here holds the same grouping. `kind` says how: " +
    '"identity" (`field` holds the value as is); "bin-source" or ' +
    '"calendar-source" (`field` holds the unbinned value, binned with the ' +
    'mark lineage\'s `binGrid` or `calendar`); "bin-output" (`field` and ' +
    '`upperField` hold a bin\'s two bounds); "calendar-output" (`field` ' +
    'holds the calendar unit, with the same `calendar`); "fold-output" ' +
    "(`field` holds the folded key and `fields` lists every folded " +
    'source field); "fold-source" (`sources` pairs each folded source ' +
    "field with a field here). Each `valueType` is the dimension's type " +
    "(nullable when the dimension is, the same epoch for an instant), and " +
    "a bin mapping's `binGrid` is the dimension's.",
  items: {
    type: "object",
    required: ["dimension", "valueType", "kind"],
    properties: {
      dimension: { type: "string" },
      valueType: VALUE_TYPE_REFERENCE,
      kind: {
        enum: [
          "identity",
          "bin-source",
          "bin-output",
          "calendar-source",
          "calendar-output",
          "fold-source",
          "fold-output",
        ],
      },
      field: { type: "string" },
      upperField: { type: "string" },
      binGrid: BIN_GRID,
      calendar: CALENDAR_GROUPING,
      fields: { type: "array", items: SOURCE_FIELD_REFERENCE },
      sources: {
        type: "array",
        items: {
          type: "object",
          required: ["source", "field"],
          properties: {
            source: SOURCE_FIELD_REFERENCE,
            field: { type: "string" },
          },
        },
      },
    },
  },
};
const GROUP_DIMENSION_MAPPINGS_REFERENCE = {
  type: "array",
  items: { type: "object" },
  description:
    "Group dimension mappings, as a panel's `selectionBindings[].dimensions` declares them.",
};

// ---------------------------------------------------------------------------
// Mark lineages, selections and interactions
// ---------------------------------------------------------------------------

const MARK_LINEAGE_ENCODING = {
  enum: [
    "x",
    "y",
    "color",
    "size",
    "row",
    "column",
    "theta",
    "text",
    "xOffset",
  ],
};

const CHART_MARKS = [
  "bar",
  "line",
  "area",
  "point",
  "arc",
  "rect",
  "boxplot",
  "text",
];

const MARK_LINEAGE = {
  type: "object",
  required: [
    "id",
    "bindingName",
    "unitViewPath",
    "markType",
    "grain",
    "markRoles",
    "dimensions",
    "series",
    "measures",
    "transforms",
  ],
  description:
    "One mark lineage: what each drawn mark of one view stands for, so an " +
    "interaction on a clicked mark can read a value the spec computes, a view " +
    "whose `transform` computes its values can show a tooltip, and the " +
    "tooltip can match the nearest value in time. List under " +
    "`dimensions` every field the chart groups or facets by. Every member, " +
    'each `grain` included, is defined in the guide chapter "mark-lineages", ' +
    'section "Mark lineages in a spec you write".',
  properties: {
    id: { type: "string" },
    bindingName: {
      type: "string",
      description: "The name of the binding the mark draws.",
    },
    unitViewPath: {
      type: "array",
      description:
        "The path inside the spec to the Vega-Lite unit view that draws " +
        'the mark, such as ["layer", 0], or [] for a spec with one view: ' +
        "object keys as strings and array positions as numbers.",
      items: {},
    },
    markType: { enum: CHART_MARKS, description: "The Vega-Lite mark type." },
    grain: {
      enum: ["row", "group", "bin", "calendar", "cell", "statistic", "scalar"],
    },
    markRoles: {
      type: "array",
      description:
        'The parts of the mark this lineage covers, at least one: ["mark"], ' +
        'or ["box", "median", "whisker", "cap", "outlier"] for a boxplot.',
      items: {
        enum: [
          "mark",
          "box",
          "median",
          "whisker",
          "cap",
          "outlier",
          "reference",
        ],
      },
    },
    dimensions: {
      type: "array",
      items: {
        type: "object",
        required: ["id", "source", "output", "valueType", "kind"],
        properties: {
          id: { type: "string" },
          source: SOURCE_FIELD,
          output: OUTPUT_FIELD,
          valueType: VALUE_TYPE_REFERENCE,
          encoding: MARK_LINEAGE_ENCODING,
          kind: {
            enum: ["identity", "bin", "calendar"],
            description:
              '"bin" requires `parameters` and `upper`; "calendar" requires `calendar`.',
          },
          upper: {
            ...OUTPUT_FIELD_REFERENCE,
            description:
              "bin only: the output field holding a bin's upper bound.",
          },
          binGrid: BIN_GRID_REFERENCE,
          calendar: CALENDAR_GROUPING_REFERENCE,
          parameters: {
            type: "object",
            required: ["kind"],
            description:
              "bin only: the spec's own bin settings. maxbins takes a " +
              "positive integer `maxbins`; step a positive `step`, with an " +
              "optional increasing `extent`; edges two or more increasing " +
              "tagged values.",
            properties: {
              kind: { enum: ["automatic", "maxbins", "step", "edges"] },
              maxbins: { type: "integer" },
              step: { type: "number" },
              extent: { type: "array", items: { type: "number" } },
              anchor: { type: "number" },
              edges: { type: "array", items: TAGGED_VALUE_REFERENCE },
            },
          },
        },
      },
    },
    series: {
      type: "array",
      items: { type: "string" },
      description: "Dimension ids, in the order a viewer reads them.",
    },
    measures: {
      type: "array",
      description:
        'A measure whose `aggregate` is "count" takes no `source`. `sourceType` is the source column\'s ' +
        "type and `valueType` the drawn value's (a mean or median is a " +
        'number whatever its source). Only a measure with role "measure" ' +
        "and additive true may be listed in `total`.",
      items: {
        type: "object",
        required: ["id", "output", "valueType", "role", "additive"],
        properties: {
          id: { type: "string" },
          source: SOURCE_FIELD_REFERENCE,
          sourceType: {
            type: "object",
            required: ["type"],
            description:
              'A value type, which here may also be {"type": "decimal", ' +
              '"precision", "scale", "bitWidth"}.',
            properties: {
              type: {
                enum: [
                  "string",
                  "boolean",
                  "number",
                  "integer",
                  "instant",
                  "decimal",
                ],
              },
              scale: { type: "integer" },
              precision: { type: "integer" },
              bitWidth: { enum: [128, 256] },
            },
          },
          output: OUTPUT_FIELD_REFERENCE,
          valueType: {
            type: "object",
            description: "As `sourceType`.",
          },
          encoding: MARK_LINEAGE_ENCODING,
          aggregate: {
            enum: ["count", "sum", "mean", "median", "min", "max", "valid"],
          },
          role: { enum: ["measure", "reference"] },
          additive: { type: "boolean" },
        },
      },
    },
    transforms: {
      type: "array",
      description:
        "The spec's own data transforms, in order, each with only its " +
        'kind\'s members: "aggregate" (dimensions, measures: ids, at least ' +
        'one measure); "bin" ' +
        'and "calendar" (dimension: an id); "stack" (measure, groupBy, ' +
        'series, offset, start, end); "fold" (fields, key, value); ' +
        '"boxplot" (source, groupBy, extent, outputs; an "iqr" extent ' +
        "requires `multiplier`).",
      items: {
        type: "object",
        required: ["kind"],
        properties: {
          kind: {
            enum: ["aggregate", "bin", "calendar", "stack", "fold", "boxplot"],
          },
          dimensions: { type: "array", items: { type: "string" } },
          measures: { type: "array", items: { type: "string" } },
          dimension: { type: "string" },
          measure: { type: "string" },
          groupBy: { type: "array", items: { type: "string" } },
          series: { type: "array", items: { type: "string" } },
          offset: { enum: ["zero", "normalize", "center"] },
          start: OUTPUT_FIELD_REFERENCE,
          end: OUTPUT_FIELD_REFERENCE,
          fields: { type: "array", items: SOURCE_FIELD_REFERENCE },
          key: OUTPUT_FIELD_REFERENCE,
          value: OUTPUT_FIELD_REFERENCE,
          source: SOURCE_FIELD_REFERENCE,
          extent: {
            type: "object",
            properties: {
              kind: { enum: ["iqr", "min-max"] },
              multiplier: { type: "number" },
            },
          },
          outputs: {
            type: "object",
            description: "Each an output field.",
            properties: {
              lower: OUTPUT_FIELD_REFERENCE,
              q1: OUTPUT_FIELD_REFERENCE,
              median: OUTPUT_FIELD_REFERENCE,
              q3: OUTPUT_FIELD_REFERENCE,
              upper: OUTPUT_FIELD_REFERENCE,
            },
          },
        },
      },
    },
    coordinate: {
      type: "object",
      required: ["source", "output", "valueType", "matching"],
      description:
        "Optional, for a continuous x such as time: the field the tooltip " +
        "matches across series, exactly or within a tolerance (`matching`). " +
        "`comparableSources` lists the source fields whose instants the " +
        "tooltip may match the x against, all counting from its `epoch`; " +
        "when the x column is an instant other than a topic's own " +
        "timestamp, it must list that column, or the panel fails with " +
        "mark-lineage-temporal-comparable-sources. `matching` and " +
        "`comparableSources` are members of this object, not of the mark " +
        "lineage.",
      properties: {
        source: SOURCE_FIELD_REFERENCE,
        output: OUTPUT_FIELD_REFERENCE,
        valueType: VALUE_TYPE_REFERENCE,
        encoding: MARK_LINEAGE_ENCODING,
        matching: {
          type: "object",
          required: ["kind"],
          properties: {
            kind: { enum: ["exact", "nearest"] },
            tolerance: {
              type: "object",
              required: ["ticks", "unit"],
              description:
                'nearest only (required there, refused with "exact"): the ' +
                'farthest a match may be, {"ticks": ' +
                '"<whole number as a decimal string>", "unit"}.',
              properties: DURATION_PROPERTIES,
            },
          },
        },
        comparableSources: {
          type: "object",
          required: ["epoch", "fields"],
          properties: {
            epoch: { type: "string" },
            fields: { type: "array", items: SOURCE_FIELD_REFERENCE },
          },
        },
      },
    },
    total: {
      type: "object",
      required: ["measures"],
      properties: {
        measures: { type: "array", items: { type: "string" } },
        label: { type: "string" },
      },
    },
  },
};

const SELECTIONS_PROPERTY = {
  type: "array",
  description:
    "Named selections that panels write through their `interactions` and read " +
    "through their `selectionBindings`. Read the guide chapter " +
    '"interactions-and-selections" before declaring one. Each entry declares the ' +
    'keys that identify what is selected: {"name", "keys": [{"key", ' +
    '"valueType"}], "resolution"?}; names are unique, and so are an ' +
    "entry's keys. " +
    "`keys` may be empty for a selection " +
    'of whole result rows. The names "time_range" and "control_values" are refused. ' +
    "`resolution` says how clauses from several panels apply together, " +
    "as on a section's `selections`. Omit this when no panel declares `interactions` " +
    "or `selectionBindings`.",
  items: {
    type: "object",
    required: ["name", "keys"],
    properties: {
      name: { type: "string" },
      keys: {
        type: "array",
        items: {
          type: "object",
          required: ["key", "valueType"],
          properties: {
            key: { type: "string" },
            valueType: VALUE_TYPE_REFERENCE,
          },
        },
      },
      resolution: { enum: ["single", "intersect", "crossfilter"] },
    },
  },
};

/** What a gesture picks out. Declared in full at `interactions[].subject`. */
const INTERACTION_SUBJECT = {
  type: "object",
  required: ["kind", "bindingName", "keys"],
  description:
    "What the gesture picks out. `bindingName` is the binding name whose stage the " +
    "values come from. `keys` are the values the interaction passes on, each " +
    '{"key": "<a name the interaction refers to>", "field": "<column>", ' +
    '"valueType"}. kind: "row" is one result row; "values" one or more ' +
    'sets of key values; "group" one rendered group of the mark lineage ' +
    '`markLineageId` names; "interval" ' +
    'a range of a number, integer or instant key, from a brush drag; "span" ' +
    "the range between two columns of the clicked row or mark, such as a " +
    "period bar's start and end: each key's `field` names the column where " +
    "the range starts (included) and `endField` the column where it ends " +
    "(excluded); its keys are number, integer or instant keys, it takes no " +
    '`markLineageId`, and it writes a range as a brush drag does; "entity" one platform ' +
    "entity, with `entityType` and exactly one key holding its id. " +
    '"values", "interval", "span" and "group" take at least one key, except a ' +
    '"group" subject of a "contributing-rows" detail whose `target` has ' +
    'empty `keys` and `dimensions`. A map\'s interactions take only "entity" ' +
    "subjects.",
  properties: {
    kind: { enum: ["row", "values", "group", "interval", "span", "entity"] },
    bindingName: { type: "string" },
    markLineageId: {
      type: "string",
      description:
        "The id of one of the panel's `markLineages`, never a " +
        "`board-derived-` id from client_read_board's markLineageOutputs, " +
        "which the page derives from the chart's views. When set, a " +
        "key's `field` names a column that mark lineage's chart computes, not a " +
        "column of the stage.",
    },
    entityType: { type: "string", enum: [...INTERACTION_ENTITY_TYPES] },
    keys: {
      type: "array",
      items: {
        type: "object",
        required: ["key", "field", "valueType"],
        properties: {
          key: { type: "string" },
          field: { type: "string" },
          endField: {
            type: "string",
            description:
              'Only in a "span" subject, where it is required: the column ' +
              "where the range ends, which the range excludes.",
          },
          valueType: VALUE_TYPE_REFERENCE,
        },
      },
    },
  },
};

const INTERACTIONS_PROPERTY = {
  type: "array",
  description:
    "What a viewer's click, menu choice or finished drag on this panel " +
    'does. Read the guide chapter "interactions-and-selections" before ' +
    "declaring interactions. Declaring `interactions`, even [], replaces the panel's " +
    "default clicks and drags, and declaring `interactions` or " +
    "`selectionBindings` changes which selections the panel reads (guide " +
    'chapter "interactions-and-selections", section "Selections and ' +
    '`selectionBindings`"). Every interaction has id, label, trigger, ' +
    "subject and kind, plus only the members of its kind: " +
    '"selection" (selection, operation, scope?) writes a declared ' +
    'selection; "control" (control, key, operation) writes a control; ' +
    '"detail" (mode, and target only with mode "contributing-rows") shows ' +
    'rows; "navigate" (destination) opens an entity or another board. ' +
    "client_read_board with proposed_definition checks " +
    "one interaction before a save.",
  items: {
    type: "object",
    required: ["id", "label", "trigger", "subject", "kind"],
    properties: {
      id: { type: "string", description: "Unique within the panel." },
      label: { type: "string" },
      trigger: {
        enum: ["primary", "secondary", "interval-complete"],
        description:
          '"primary" is a click on a mark, row or path; "secondary" an ' +
          'entry in a menu; "interval-complete" a finished brush drag. An ' +
          '"interval" subject takes "interval-complete", and no other ' +
          'subject does. A panel takes at most one "primary" and one ' +
          '"interval-complete" interaction.',
      },
      subject: INTERACTION_SUBJECT,
      kind: { enum: ["selection", "control", "detail", "navigate"] },
      selection: {
        type: "string",
        description:
          "selection only: the name of the declared selection written. " +
          "The subject's keys are exactly the selection's keys, with " +
          "compatible value types; a selection with no keys takes a " +
          '"row" subject and "replace", "toggle" or "clear".',
      },
      scope: {
        enum: ["local", "outer"],
        description:
          'selection only: "outer" writes the board\'s selection from a ' +
          'panel inside a section; "local" (the default) writes where the ' +
          "panel sits.",
      },
      control: {
        type: "string",
        description:
          "control only: the name of the value control written, whose " +
          'type the key\'s `valueType` must fit; "toggle", "add" and ' +
          '"remove" need one with "multi": true.',
      },
      key: {
        type: "string",
        description:
          "control only: the key of the interaction's subject whose value is written.",
      },
      operation: {
        enum: ["replace", "toggle", "add", "remove", "clear", "reset"],
        description: 'selection and control only; "reset" is control only.',
      },
      mode: {
        enum: ["output-row", "matching-output", "contributing-rows"],
        description:
          'detail only: "output-row" shows the clicked row and needs a ' +
          '"row" subject; "matching-output" shows the rows of the same ' +
          "result that match the subject's keys and needs at least one subject key; " +
          '"contributing-rows" shows rows of the stage named in `target`.',
      },
      target: {
        type: "object",
        required: ["stage", "keys"],
        description:
          'detail with mode "contributing-rows" only, and required there: ' +
          '{"stage": "<id of ' +
          'the stage whose rows are shown>", "keys": [{"sourceKey": ' +
          '"<subject key>", "targetField": "<column of that stage>"}]}, ' +
          "mapping each subject key once, and optionally the selections " +
          "those rows also follow.",
        properties: {
          stage: { type: "string" },
          keys: {
            type: "array",
            items: {
              type: "object",
              required: ["sourceKey", "targetField"],
              properties: {
                sourceKey: { type: "string" },
                targetField: { type: "string" },
              },
            },
          },
          selectionBindings: {
            type: "array",
            items: { type: "object" },
            description:
              "As the panel's `selectionBindings`, without `bindingName`.",
          },
          dimensions: GROUP_DIMENSION_MAPPINGS_REFERENCE,
        },
      },
      destination: {
        type: "object",
        required: ["kind"],
        description:
          'navigate only: {"kind": "entity", "entityType", "idKey": ' +
          '"<subject key holding the id>"}, or {"kind": "board", ' +
          '"boardId", "inputs": [{"control": "<control of that board>", ' +
          '"sourceKey": "<subject key>"}], "includeTimeRange"?}. `idKey` names ' +
          'a "string" key; "events" opens the visualizer on the event with that id.',
        properties: {
          kind: { enum: ["board", "entity"] },
          entityType: { type: "string", enum: [...INTERACTION_ENTITY_TYPES] },
          idKey: { type: "string" },
          boardId: { type: "string" },
          includeTimeRange: { type: "boolean" },
          inputs: {
            type: "array",
            items: {
              type: "object",
              required: ["control", "sourceKey"],
              properties: {
                control: { type: "string" },
                sourceKey: { type: "string" },
              },
            },
          },
        },
      },
    },
  },
};

/** The most decimal places a pivot value may round its cells to. */
const PIVOT_MAX_DECIMALS = 12;

const PANEL_SCHEMA: Record<string, unknown> = {
  type: "object",
  required: ["kind", "id", "bindings"],
  description:
    "One panel. Set `kind` and include only that kind's members; an " +
    "unknown member or one of another kind makes the panel invalid. A " +
    "panel draws stages through `bindings`, one entry per binding name. " +
    '"chart" requires `template` or `spec`. "table" ' +
    'shows one stage (optional `columns`, optional `pivot`). "map" draws ' +
    "each binding's rows as paths from their `latitude` and `longitude` " +
    "columns, both numbers or both lists of numbers, one path per value " +
    "of the binding's group column (optional `pathStyle`).",
  properties: {
    kind: { type: "string", enum: ["chart", "table", "map"] },
    interactions: INTERACTIONS_PROPERTY,
    selectionBindings: {
      type: "array",
      description:
        "The selections this panel reads, one entry per selection and " +
        'binding: {"bindingName": "<binding name>", "selection": "<name>", "keys": ' +
        '[{"key": "<a key of the selection>", "field": "<column of that ' +
        'binding\'s stage>"}], "dimensions"?}, mapping every key of the ' +
        'selection. Read the guide chapter "interactions-and-selections" before ' +
        "declaring it.",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["bindingName", "selection", "keys"],
        properties: {
          bindingName: { type: "string" },
          selection: { type: "string" },
          keys: {
            type: "array",
            items: {
              type: "object",
              required: ["key", "field"],
              properties: {
                key: { type: "string" },
                field: { type: "string" },
              },
            },
          },
          dimensions: GROUP_DIMENSION_MAPPINGS,
        },
      },
    },
    markLineages: {
      type: "array",
      minItems: 1,
      description:
        "chart only (optional): mark lineages, each describing one mark " +
        'layer of the chart. A "builder" template writes this member ' +
        "itself, except on a chart whose time field definitions lack " +
        'the keys guide chapter "mark-lineages" lists. Each mark lineage ' +
        "is checked against the spec and the stage's columns; most " +
        "mismatches block the save, and the dry " +
        "run reports the rest as warnings. Read the guide " +
        'chapter "mark-lineages" before writing one.',
      items: MARK_LINEAGE,
    },
    id: {
      type: "string",
      description: "Unique within the board (slug style).",
    },
    title: {
      type: "string",
      description:
        "Shown in the panel's title bar; may reference a control as " +
        "${name} or ${name.label}, replaced with the viewer's choice.",
    },
    placement: {
      type: "object",
      description:
        "Where the panel sits on the canvas and how large it is, in canvas " +
        "units (one unit is one screen pixel at 100% zoom), with the origin " +
        "at the top left and y growing downwards. `x` and `y` are measured " +
        "from the top-left corner of the section named by `section` when " +
        "there is one, and from the canvas origin otherwise. Give all four " +
        "of x, y, width and height, or omit `placement`, except that a " +
        "panel a client_update_board add operation adds may give `section` " +
        "and no x or y, optionally with width and height, and the tool " +
        'places it in that section (guide chapter "authoring-workflow", ' +
        'section "Sections and layout"). A panel with no placement is ' +
        "shown below the placed ones at 960 by 600. The smallest size is " +
        "144 by 120. Positions may be negative: the canvas is unbounded.",
      properties: {
        x: { type: "number" },
        y: { type: "number" },
        width: { type: "number", minimum: 144 },
        height: { type: "number", minimum: 120 },
        section: {
          type: "string",
          description: "The id of a section in the board's sections array.",
        },
      },
    },
    bindings: {
      type: "array",
      description:
        "One entry per stage the panel reads, in the order it draws them: " +
        '{"name": "<the panel\'s own name for the stage\'s rows>", "stage": ' +
        '"<a stage\'s id>"}, each name used once. A chart with a `spec` has one binding per ' +
        "Vega-Lite dataset name the spec references. A chart with a " +
        "`template` and no `spec` sends []: the board tools write its one " +
        'binding, named "rows", from `template.params.stage` and replace whatever is ' +
        "sent. A table has exactly one binding, whose name nothing reads. " +
        "A map has one binding per set of paths, in legend order, and " +
        "`pathStyle` and `coordinateUnits` are keyed by binding name. Two bindings " +
        'may name one stage. A map\'s entry may also carry "group" and ' +
        '"line".',
      items: {
        type: "object",
        required: ["name", "stage"],
        properties: {
          name: { type: "string" },
          stage: { type: "string" },
          group: {
            type: "object",
            required: ["id"],
            description:
              "map only: the column whose values divide the stage's rows " +
              "into paths, each with its own color and legend row " +
              '("id"), the column that labels them ("name", optional), ' +
              'and "entity", the kind of platform entity the id column ' +
              "holds the ids of, when it holds one; a path then offers " +
              "the panel's interactions on that kind of entity. Omit `group` " +
              "to group by `session_id` and label by `session_name`. " +
              '(Unrelated to the "group" stage kind.)',
            properties: {
              id: { type: "string", minLength: 1 },
              name: { type: "string", minLength: 1 },
              entity: {
                type: "string",
                enum: [...INTERACTION_ENTITY_TYPES],
              },
            },
          },
          line: {
            type: "string",
            description:
              "map only: a column whose change of value, within one path, " +
              "starts a new line with nothing joining it to the line " +
              "before. Not allowed when the coordinates are lists, where " +
              "each row is already one line.",
          },
        },
      },
    },
    spec: {
      type: "object",
      description:
        "chart only (required unless template is given): a Vega-Lite spec " +
        "object of at most 100,000 characters that reads each binding " +
        'only as "data": {"name": "<binding name>"}, never as inline ' +
        'values or a url. Read the guide chapter "chart-specs" before ' +
        "writing one: general Vega-Lite knowledge does not cover how this " +
        "page draws and sizes a spec. Send a template instead whenever one " +
        'draws the chart (guide chapter "chart-templates", section "What ' +
        'needs a spec you write").',
    },
    columns: {
      type: "array",
      description:
        'table only (optional): [{"name": "<result column>", "label"?}]; ' +
        "omit to show every column. (A combine stage's `columns` and a " +
        "pivot's `columns` are different members.)",
    },
    participation: {
      type: "object",
      description:
        "Optional: how the panel responds to each selection it reads, " +
        'keyed by the selection\'s name ("focus" and "brush" on a panel ' +
        "with neither `interactions` nor `selectionBindings`, otherwise the " +
        'names in its `selectionBindings`): "filter", "highlight" (each ' +
        "row the panel draws gets a boolean field in_<name>, such as " +
        "in_focus, which a spec reads to fade or emphasize marks) " +
        'or "ignore". Omitted, a chart or table filters by each selection, ' +
        "and a map highlights by `focus` and ignores `brush`. What each mode does is in " +
        'the guide chapter "focus-and-brush", ' +
        'section "How a panel responds: `participation`".',
      additionalProperties: {
        type: "string",
        enum: ["filter", "highlight", "ignore"],
      },
    },
    publish: {
      type: "object",
      description:
        'Optional: keyed by "focus" or "brush"; "outer" makes the panel\'s ' +
        "gestures write the board's selection of that name from inside a " +
        "section that declares its own. Omit it to write where the panel " +
        'sits (guide chapter "focus-and-brush", section "Sections with ' +
        'their own `focus` or `brush` selection").',
      additionalProperties: {
        type: "string",
        enum: ["outer"],
      },
    },
    pivot: {
      type: "object",
      description:
        "table only (optional): regroups the rows of the table's stage " +
        'for display inside this table only: {"rows", "columns"?, ' +
        '"values", "sort"?, "filter"?, "derived"?, "totals"?}. Read the ' +
        'guide chapter "pivot-tables" before writing one. ' +
        '"rows" may be empty only when "columns" is ' +
        "given, and a field is a row field or the split field, never both. " +
        "The panel's \"columns\" then selects from the pivot's output " +
        "columns.",
      properties: {
        rows: {
          type: "array",
          items: { type: "string" },
          description:
            "The fields to group by, outer level first, each once, named " +
            "as the stage's result columns or as derived columns.",
        },
        columns: {
          type: "object",
          description:
            "The one field whose values become columns of their own; give " +
            '"values" to pin which of them do, and omit it to take whatever ' +
            "values the data has.",
          properties: {
            field: { type: "string" },
            values: { type: "array" },
          },
        },
        values: {
          type: "array",
          description:
            "What each group is aggregated into; at least one. Labels, " +
            "given or default, are distinct and differ from the row fields.",
          items: {
            type: "object",
            required: ["field", "aggregate"],
            properties: {
              field: { type: "string" },
              aggregate: {
                type: "string",
                enum: ["count", "sum", "mean", "median", "min", "max", "valid"],
                description:
                  '"count" counts rows and takes an empty field; "valid" ' +
                  "counts the rows where its field has a value.",
              },
              label: {
                type: "string",
                description:
                  "Names the output column; defaults to the aggregate and " +
                  "the field.",
              },
              decimals: {
                type: "integer",
                minimum: 0,
                maximum: PIVOT_MAX_DECIMALS,
                description:
                  "Rounds the cells to this many decimal places; omit it " +
                  "to keep full precision. Taken by count, valid, " +
                  "sum, mean, and median only, and not accepted with a " +
                  '"columns" split.',
              },
            },
          },
        },
        sort: {
          type: "array",
          description:
            'Each {"field", "order"}, where `field` is a row field or, ' +
            "without a split, a value's label. The member is `order` here, " +
            "where a stage's `orderBy` and an entity.query `sort` say " +
            "`direction`.",
          items: {
            type: "object",
            properties: {
              field: { type: "string" },
              order: { type: "string", enum: ["ascending", "descending"] },
            },
          },
        },
        filter: {
          type: "array",
          description:
            'Each {"field", "op", "value"}, applied before grouping. ' +
            '"value" is a string, number or boolean written in the ' +
            'field\'s type, and is left out of "is null" and "is not null".',
          items: {
            type: "object",
            properties: {
              field: { type: "string" },
              op: {
                type: "string",
                enum: [
                  "=",
                  "!=",
                  "<",
                  "<=",
                  ">",
                  ">=",
                  "contains",
                  "is null",
                  "is not null",
                ],
              },
            },
          },
        },
        derived: {
          type: "array",
          description:
            'Each {"name", "sql"}: a column computed per row before the ' +
            "filter and the grouping, from one DuckDB scalar expression over " +
            "the stage's columns. A name may not repeat a stage column's.",
          items: {
            type: "object",
            properties: {
              name: { type: "string" },
              sql: { type: "string" },
            },
          },
        },
        totals: {
          type: "string",
          enum: ["rollup"],
          description:
            '"rollup" adds a subtotal row per row level and one grand ' +
            "total; omit it for detail rows alone. Refused when `rows` is " +
            "empty.",
        },
      },
    },
    pathStyle: {
      type: "object",
      description:
        'map only (optional): binding name to {"lineColor"?, "label"?} styling ' +
        "for the paths drawn from that binding.",
    },
    coordinateUnits: {
      type: "object",
      description:
        'map only (optional): binding name to {"latitude"?, "longitude"?}, ' +
        'each "deg_float" (degrees) or "deg_int_1e7" (degrees times ' +
        "10,000,000); a coordinate with no unit is read by its column's " +
        'type (guide chapter "topic-data-and-maps", section "The map panel").',
    },
    template: {
      type: "object",
      description:
        "chart only (optional): the chart as a template, in place of " +
        '`spec`: {"name": "builder" | "overview-detail", "params": {...}}. ' +
        'builder params: {"stage", "mark", "encoding", "brush", ' +
        '"point"?, "innerRadius"?, "boxplotExtent"?, "colorScheme"?, ' +
        '"xDomain"?, "yDomain"?}, with "encoding" keyed by channel. ' +
        'overview-detail params, all required: {"stage", "x", "y", ' +
        '"detailMark", "overviewMark", "tooltip", "publish"}. "stage" holds ' +
        "a stage id. Each encoding channel, and overview-detail's x and y, " +
        "is a field definition " +
        '{"field", "type", "aggregate"?, "bin"?, "timeUnit"?, "sort"?, ' +
        '"stack"?, "title"?, "labelAngle"?, "labelFontSize"?, ' +
        '"labelLimit"?, "ticks"?, "domain"?, "tickCount"?, "tickMinStep"?, ' +
        '"valueType"?, "sourceType"?, "matching"?, ' +
        '"comparableSources"?}. "timeUnit" buckets follow the board\'s ' +
        "calendar; the last four refine the builder's mark " +
        'lineage (guide chapter "mark-lineages"). ' +
        "A key or value the template does not take fails the panel with " +
        "chart-template-failed. What each param means and every value it " +
        'takes are in the guide chapter "chart-templates". ' +
        "A panel sent with both template and spec is stored as sent and " +
        "draws its spec.",
      properties: {
        name: { type: "string", enum: ["builder", "overview-detail"] },
        params: {
          type: "object",
          description:
            'The template\'s parameters, per the guide chapter "chart-templates".',
        },
      },
    },
  },
};

/** The keys every definition carries: the `required` list of both flat schemas below. */
const REQUIRED_DEFINITION_KEYS = [
  "schema_version",
  "time_range",
  "panels",
] as const;

const SCHEMA_VERSION_PROPERTY: Record<string, unknown> = {
  type: "integer",
  enum: [1],
  description: "Always 1.",
};

const CALENDAR_PROPERTY: Record<string, unknown> = {
  type: "object",
  description:
    'The board\'s calendar: {"time_zone"?, "week_start"?}. "time_zone" is ' +
    "an IANA time zone name, spelled with the capital letters the IANA " +
    "database gives it, such as " +
    '"America/Los_Angeles" or "UTC" (the default), and not an offset such ' +
    'as "+05:30" or one of "CET", "EET", "MET" and "WET"; "week_start" is ' +
    'the day weeks start on, "monday" to "sunday" (default "monday"). The ' +
    "board shows every time in this zone, groups time units, weeks, " +
    "calendar selections and metric aggregate periods by it, and reads a " +
    "custom time range's dates and times in it (guide chapter " +
    '"authoring-workflow", section "The definition\'s top-level members").',
  properties: {
    time_zone: { type: "string" },
    week_start: { enum: [...DAYS_OF_WEEK] },
  },
};

const TITLE_PROPERTY: Record<string, unknown> = {
  type: "string",
  description:
    "Board title, shown in the header and the board list. A definition " +
    "without one is saved as an untitled board.",
};

const SECTIONS_PROPERTY: Record<string, unknown> = {
  type: "array",
  description:
    "Titled regions of the canvas. A panel joins a section through " +
    "`placement.section`. Section ids are unique within the board; " +
    "sections do not nest and hold no list of their members. Layout is " +
    'in the guide chapter "authoring-workflow", section "Sections and ' +
    'layout". Omit this when the board has no sections.',
  items: {
    type: "object",
    required: ["id", "title", "x", "y", "width", "height"],
    properties: {
      id: {
        type: "string",
        description: "Unique within the board (slug style).",
      },
      title: { type: "string" },
      x: { type: "number" },
      y: { type: "number" },
      width: { type: "number", minimum: 240 },
      height: { type: "number", minimum: 160 },
      selections: {
        type: "array",
        description:
          "Optional: the selections this section declares for itself, each " +
          '{"name", "resolution"?}, where name is "focus", "brush" or a ' +
          "selection the definition's `selections` declares, never " +
          '"time_range" or "control_values". A panel inside the section then reads ' +
          "and writes that selection at the section instead of at the " +
          'board (guide chapter "focus-and-brush", section "Sections with ' +
          'their own `focus` or `brush` selection"). `resolution`: ' +
          '"single" keeps only the latest clause; "intersect" and ' +
          '"crossfilter" keep one clause per writing panel and apply each ' +
          "to every panel but the one that wrote it; omitted, the board's " +
          "rule for that name applies. Omit `selections` for a section " +
          "that is only layout.",
        items: {
          type: "object",
          required: ["name"],
          properties: {
            name: { type: "string" },
            resolution: {
              type: "string",
              enum: ["single", "intersect", "crossfilter"],
            },
          },
        },
      },
    },
  },
};

const STAGES_PROPERTY: Record<string, unknown> = {
  type: "array",
  description:
    "The board's stages: one flat ordered list. Each stage is declared " +
    "once and produces one table, which other stages read through their " +
    "`inputs` and any number of panels draw through their `bindings`. " +
    "Three strings are easy to confuse: a stage's `id` (what `inputs`, " +
    "`bindings`, a chart template's `params.stage` and " +
    "client_update_board operations use), a stage's `name` (the table " +
    "name SQL reads), and a panel binding's `name`.",
  items: {
    type: "object",
    required: ["id", "name", "body"],
    properties: {
      id: {
        type: "string",
        description:
          "Unique within the board and compared exactly: a letter or " +
          "digit, then letters, digits, underscores and hyphens. A rename " +
          "keeps it.",
      },
      name: {
        type: "string",
        description:
          "Unique within the board without regard to case: the stage's " +
          "table is registered under it, so a SQL stage reads an input " +
          "by this name. Same characters as an id; a control's name, " +
          "which text writes as $name, is a letter, then letters, digits " +
          "and underscores.",
      },
      inputs: {
        type: "array",
        items: { type: "string" },
        description:
          "The ids of the stages this one reads, in order (not [], and no " +
          "cycles). State it on " +
          "every stage that reads another; omitted, it is the nearest " +
          "stage before this one in the list that is not a control. The " +
          "four kinds that read the platform (entity.query, " +
          "metric.aggregate, metric.values, topic.data) take none. A " +
          "control takes none, except a value " +
          'control whose `domain` is a "column", which names the one stage ' +
          "holding that column. A control-filter names two: the stage " +
          "whose rows it filters, then the value control.",
      },
      body: STAGE_BODY_SCHEMA,
    },
  },
};

/** The flat JSON Schema `client_create_board` declares as its input. */
export const boardDefinitionInputSchema: Record<string, unknown> = {
  type: "object",
  required: [...REQUIRED_DEFINITION_KEYS],
  properties: {
    schema_version: SCHEMA_VERSION_PROPERTY,
    title: TITLE_PROPERTY,
    time_range: {
      description: TIME_RANGE_DESCRIPTION,
    },
    calendar: CALENDAR_PROPERTY,
    sections: SECTIONS_PROPERTY,
    panels: {
      type: "array",
      description: "The board's panels.",
      items: PANEL_SCHEMA,
    },
    selections: SELECTIONS_PROPERTY,
    stages: STAGES_PROPERTY,
  },
};

/** A list member of the definition, declared by pointing at the create tool's schema for it. */
function listAsCreateDeclares(key: string): Record<string, unknown> {
  return {
    type: "array",
    items: { type: "object" },
    description: `As client_create_board's ${key}.`,
  };
}

/**
 * The flat JSON Schema `client_dry_run_board` declares as its input: the
 * create tool's top-level and required keys, each pointing at the create
 * tool's schema for its shape, and the saved board the definition describes.
 */
export const dryRunBoardInputSchema: Record<string, unknown> = {
  type: "object",
  required: [...REQUIRED_DEFINITION_KEYS],
  properties: {
    board_id: {
      type: "string",
      description:
        "The saved board this definition describes, from misc_context.board or client_read_board. Omit it to check a new board.",
    },
    schema_version: SCHEMA_VERSION_PROPERTY,
    title: { type: "string" },
    time_range: { description: "As client_create_board's time_range." },
    calendar: { description: "As client_create_board's calendar." },
    sections: listAsCreateDeclares("sections"),
    panels: listAsCreateDeclares("panels"),
    selections: listAsCreateDeclares("selections"),
    stages: listAsCreateDeclares("stages"),
  },
};

// ---------------------------------------------------------------------------
// Definition shape check
//
// Structural checks only, with specific messages the model can act on;
// everything deeper (time range values, panel schemas, chart binding rules) is
// checked by the dry run. Shared by the board tools and the create
// confirmation card so the tool result and the card name the same problem.
// ---------------------------------------------------------------------------

/**
 * The definition a create or dry-run call carries. Both tools declare the
 * definition as the input itself, but models sometimes wrap it in a
 * `definition` key; an object under that key is taken as the definition.
 * Any other input is returned as is, and `describeDefinitionShapeProblem`
 * names a wrapper it finds there.
 */
export function definitionFromToolInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  return isPlainObject(input.definition) ? input.definition : input;
}

/** The `client_create_board` input member that says the user asked for a
 *  new board. It sits beside the definition's members and is not one. */
export const USER_ASKED_FOR_NEW_BOARD = "user_asked_for_new_board";

/**
 * The definition a `client_create_board` call carries: its input without the
 * fields the server adds to every tool's input and without
 * `user_asked_for_new_board`, read as {@link definitionFromToolInput} reads
 * it. Both come off because the dry run's strict parse refuses unknown
 * top-level keys. The tool and its confirmation card both read the
 * definition here, so the card dry-runs the definition the tool saves.
 */
export function definitionFromCreateInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const fields = withoutServerInjectedFields(input);
  // A non-object input goes on unchanged, for the shape check to name;
  // destructuring an array would turn it into an object keyed by index.
  if (!isPlainObject(fields)) {
    return fields;
  }
  const { [USER_ASKED_FOR_NEW_BOARD]: _userAskedForNewBoard, ...definition } =
    fields;
  return definitionFromToolInput(definition);
}

export function isPlainObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The definition's top-level keys, in the order the schema declares them, for messages. */
const TOP_LEVEL_KEYS = Object.keys(
  boardDefinitionInputSchema.properties as Record<string, unknown>,
).join(", ");

/** A JSON value's kind with its article, for a message naming what a key held. */
function describeJsonValue(value: unknown): string {
  if (value === null) {
    return "null";
  }
  return Array.isArray(value) ? "an array" : `a ${typeof value}`;
}

/** The first key whose value is an object carrying `schema_version`: the wrapper a definition sits under. */
function wrapperKeyOf(value: Record<string, unknown>): string | undefined {
  return Object.keys(value).find((key) => {
    const inner = value[key];
    return isPlainObject(inner) && "schema_version" in inner;
  });
}

/** `message` tells the model what to fix; `member` names the top-level key
 *  at fault, and is absent when the value is not a JSON object at all. */
export interface DefinitionShapeProblem {
  message: string;
  member?: string;
}

/**
 * Why `value` is not shaped like a board definition, or `null` when it is. A
 * definition found under a wrapper key (one `definitionFromToolInput` does
 * not unwrap) is reported by that key's name, so the model knows which key
 * to remove. Unknown top-level keys pass this check; the dry run's strict
 * parse reports them.
 */
export function describeDefinitionShapeProblem(
  value: unknown,
): DefinitionShapeProblem | null {
  if (!isPlainObject(value)) {
    return {
      message: `The board definition must be a JSON object with the keys ${REQUIRED_DEFINITION_KEYS.join(", ")}.`,
    };
  }
  if ("definition" in value && !isPlainObject(value.definition)) {
    return {
      member: "definition",
      message:
        `The "definition" key holds ${describeJsonValue(value.definition)}, ` +
        `not a definition object. Send the definition's keys at the top level instead: ${TOP_LEVEL_KEYS}.`,
    };
  }
  if (value.schema_version !== 1) {
    const wrapperKey = wrapperKeyOf(value);
    if (wrapperKey !== undefined) {
      return {
        member: wrapperKey,
        message: `The definition sits under the key "${wrapperKey}". Send its keys at the top level instead: ${TOP_LEVEL_KEYS}.`,
      };
    }
    return {
      member: "schema_version",
      message: 'The definition must carry "schema_version": 1.',
    };
  }
  if (!("time_range" in value)) {
    return {
      member: "time_range",
      message:
        'The definition must carry a "time_range": a preset string ' +
        `(${PRESET_RANGE_LIST}), a custom date range, or a relative range.`,
    };
  }
  if (!Array.isArray(value.panels)) {
    return {
      member: "panels",
      message: 'The definition must carry a "panels" array.',
    };
  }
  return null;
}
